import { configureSFXEvents } from './sfx-bus';
import { AudioResources, SFXEvents } from '@game-libs/models';
import { GlobalGameStore } from '@game-libs/global-game-store';
import { throttle } from '@game-libs/exc-libs/src/helpers/functions.helper';

export type AudioManager = {
  playMusicTrack: (
    track: keyof AudioResources['Music'],
    volume?: number
  ) => void;
  playSFX: (track: SFXEvents, volume?: number) => void;
  playSFXNow: (track: SFXEvents, volume?: number) => void;
  playTyping: (track: string, volume?: number) => void;
  stop: (
    track: keyof AudioResources['Music'] | keyof AudioResources['SFX']
  ) => void;
  mute: () => void;
  unmute: () => void;
};

export const AudioManager = (resources?: AudioResources): AudioManager => {
  let instance: AudioManager | null = (window as any).audio_manager || null;
  if (instance) {
    return instance;
  } else {
    if (!resources) {
      throw new Error(
        'Resources SFX and Music must be provided on the first initialization of the AudioManager. \n⚠️ Call AudioManager() before the usage of any of its methods.'
      );
    }
  }

  let isMuted = false;
  let masterVolume = 1;
  let musicVolume = 0.25;
  let sfxVolume = 0.5;

  const { settings: storeSettings } = GlobalGameStore();

  storeSettings.subscribe((settings) => {
    musicVolume = settings.musicVolume / 100;
    sfxVolume = settings.sfxVolume / 100;
    isMuted = settings.isMuted;

    Object.values(resources.Music).map((track) => {
      track.volume = musicVolume;
      if (isMuted) {
        track.volume = 0;
      }
    });

    // Object.values(resources.SFX).map((track) => {
    //   track.volume = sfxVolume;
    // });

    // Object.values(resources.TypingSFX).map((track) => {
    //   if (track) {
    //     track.volume = sfxVolume;
    //   }
    // });
  });

  const mute = () => {
    isMuted = true;
  };

  const unmute = () => {
    isMuted = false;
  };

  const stop = (track: keyof AudioResources['Music'] | SFXEvents) => {
    if (resources.Music[track]) {
      resources.Music[track].stop();
    } else if (resources.SFX[track as SFXEvents]) {
      resources.SFX[track as SFXEvents]?.stop();
    }
  };

  const playMusicTrack = (track: keyof AudioResources['Music'], volume = 1) => {
    if (isMuted) return;
    if (resources.Music[track]) {
      resources.Music[track].play(volume * masterVolume * musicVolume);
    }
  };

  const playSFX = throttle((track: SFXEvents, volume = 1) => {
    if (isMuted) return;

    resources.SFX[track]?.play(
      volume * masterVolume * sfxVolume
    );
  }, 75);

  const playSFXNow = (track: SFXEvents, volume = 1) => {
    if (isMuted) return;

    resources.SFX[track]?.play(
      volume * masterVolume * sfxVolume
    );
  };

  const playTyping = (speaker: string, volume = 1) => {
    if (isMuted) return;

    const speakerSFX = resources.TypingSFX[speaker] ? speaker : 'default';

    resources.TypingSFX[speakerSFX]?.play(
      volume * masterVolume * sfxVolume
    );
  };

  configureSFXEvents(playSFX);

  instance = {
    playSFX,
    playSFXNow,
    playTyping,
    playMusicTrack,
    stop,
    mute,
    unmute,
  };

  (window as any).audio_manager = instance;

  return instance;
};
